import React, { useCallback, useEffect, useMemo, useState } from "react";
import "../../pages/Dashboard.scss";
import agentIcon from "../../assets/images/agent-icon.svg";
import launchArrow from "../../assets/images/ic_carousalArrowRight.svg";
import leftArrow from "../../assets/images/ic_carousalArrowLeft.svg";
import rightArrow from "../../assets/images/ic_carousalArrowRight.svg";
import agentBg1 from "../../assets/images/agent-bg1.png";
import featuredAgent1 from "../../assets/images/featuredagent1.png";
import featuredAgent2 from "../../assets/images/featuredagent2.png";
import featuredAgent3 from "../../assets/images/featuredagent3.png";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import { selectAgent } from "../../store/userSlice";
import { Agents } from "../../types/master";
import { useNavigate } from "react-router-dom";
import agentIcon1 from "../../assets/images/ic_agent1.svg";
import agentIcon2 from "../../assets/images/ic_agent2.svg";
import { v4 as uuid } from "uuid";
import { Modal } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import AgentInfoModal from "../AgentInfoModal";

export type Agent = {
  id: string;
  title: string;
  subtitle?: string;
};

type FeaturedAgentsProps = {
  itemsPerSlide?: number;
  onAgentClick: (agentId: number) => void;
};

const images = import.meta.glob("../../assets/images/*.png", {
  eager: true,
  as: "url",
});

// Agents that display "Coming Soon" tag
const COMING_SOON_AGENTS = [
  "RFP Responder",
  " Smart Meeting Brief",
  "Trend Watch: News and Tech",
  "Executive Operator",
];

export default function FeaturedAgents({
  itemsPerSlide = 3,
  onAgentClick,
}: FeaturedAgentsProps): JSX.Element {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [openAgentModal, setOpenAgentModal] = useState<boolean>(false);
  const [agentId, setAgentId] = useState<string | number | null>(null);

  // Helper function to construct video blob URL
  const getVideoUrl = useCallback((agent: Agents): string | null => {
    if (!agent.agent_intro_video_path) return null;
    return `${import.meta.env.VITE_TEMPLATE_BLOB_URL}/intro-videos/${agent.agent_intro_video_path}?${import.meta.env.VITE_TEMPLATE_G42_FILES}`;
  }, []);

  const agentCardBg = [
    "linear-gradient(135deg, #003B7A 0%, #003876 8.33%, #003672 16.67%, #00346E 25%, #00316B 33.33%, #002F67 41.67%, #002C63 50%, #002A5F 58.33%, #00275C 66.67%, #002558 75%, #002354 83.33%, #002051 91.67%, #001E4D 100%)",
    "linear-gradient(135deg, #0066CC 0%, #006BCE 7.14%, #006FCF 14.29%, #0073D1 21.43%, #0078D2 28.57%, #007CD4 35.71%, #0081D5 42.86%, #0085D6 50%, #0089D8 57.14%, #008ED9 64.29%, #0092DB 71.43%, #0096DC 78.57%, #009ADD 85.71%, #009FDF 92.86%, #00A3E0 100%)",
  ];

  const imageClasses = [
    "agent-one",
    "agent-two",
    "agent-three",
    "agent-four",
    "agent-five",
  ];

  const agentIcons = [agentIcon1, agentIcon2];

  // Featured agent background images - cycle through them
  const featuredAgentBgs = [featuredAgent1, featuredAgent2, featuredAgent3];

  // store
  const allAgents = useSelector((state: RootState) => state.agents.data);

  const displayedAgents = useMemo(() => {
    return allAgents.filter((agent) => !!agent.is_featured);
  }, [allAgents]);

  // state - track current position (0 means showing first N images)
  const [currentSlide, setCurrentSlide] = useState(0);
  const [previewVideoAgentId, setPreviewVideoAgentId] = useState<number | null>(
    null,
  );
  const [openVideoAgentId, setOpenVideoAgentId] = useState<number | null>(null);

  // Responsive: 4 slides when width >= 1380px, 3 slides when < 1380px
  const [imagesPerView, setImagesPerView] = useState(
    typeof window !== "undefined" && window.innerWidth >= 1380 ? 4 : 3,
  );

  const [wrapperWidth, setWrapperWidth] = useState(0);
  const carouselWrapperRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleResize = () => {
      setImagesPerView(window.innerWidth >= 1380 ? 4 : 3);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Measure carousel wrapper width for exact slide sizing (no peek of next slide)
  useEffect(() => {
    const el = carouselWrapperRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      setWrapperWidth(el.offsetWidth);
    });
    ro.observe(el);
    setWrapperWidth(el.offsetWidth);
    return () => ro.disconnect();
  }, [imagesPerView]);

  // Clamp current slide when imagesPerView changes (e.g. 4 -> 3)
  useEffect(() => {
    const max = Math.max(0, displayedAgents.length - imagesPerView + 1);
    setCurrentSlide((prev) => (max <= 1 ? 0 : Math.min(prev, max - 1)));
  }, [imagesPerView, displayedAgents.length]);

  // Calculate how many positions we can slide (total images - visible images + 1)
  const maxSlides = Math.max(0, displayedAgents.length - imagesPerView + 1);

  // Exact sizing when wrapper is measured: card width and step in px so exactly N slides show (no peek)
  const GAP_PX = 14;
  const cardWidthPx =
    wrapperWidth > 0
      ? (wrapperWidth - (imagesPerView - 1) * GAP_PX) / imagesPerView
      : 0;
  // Step = one card + gap per slide (slide 0 = cards 0..3, slide 1 = cards 1..4, etc.)
  const slideStepPx = wrapperWidth > 0 ? cardWidthPx + GAP_PX : 0;
  const listWidthPx =
    wrapperWidth > 0 && displayedAgents.length > 0
      ? displayedAgents.length * cardWidthPx +
        (displayedAgents.length - 1) * GAP_PX
      : 0;
  const useExactSizing = wrapperWidth > 0 && listWidthPx > 0;
  // Cap transform so we never show empty space to the right (rounding-safe)
  const maxTranslatePx = Math.max(0, listWidthPx - wrapperWidth);
  const translatePx = useExactSizing
    ? Math.min(currentSlide * slideStepPx, maxTranslatePx)
    : 0;
  useEffect(() => {
    if (maxSlides <= 1) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev >= maxSlides - 1 ? 0 : prev + 1));
    }, 12222000);

    return () => clearInterval(interval);
  }, [maxSlides]);

  // Move to next set of images
  const nextSlide = () => {
    if (currentSlide < maxSlides - 1) {
      setCurrentSlide((prev) => prev + 1);
    }
  };

  // Move to previous set of images
  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide((prev) => prev - 1);
    }
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const handleSelectAgent = useCallback(
    (agent: Agents) => {
      onAgentClick(agent.id);
    },
    [onAgentClick],
  );

  // Helper function to check if agent is available for launch
  const isAgentLaunchable = useCallback((agent: Agents): boolean => {
    // Hidden agents (status = 0) keep their card visible but Launch stays disabled
    if (agent.status !== 1) {
      return false;
    }

    // Agent IDs that should open using their endpoint URL in a new tab
    const EXTERNAL_ENDPOINT_AGENTS = [19, 27, 31, 32, 33, 34, 503, 504, 505, 506, 507];

    // Agent IDs that should have launch button disabled
    const DISABLED_LAUNCH_AGENTS: number[] = [];

    // If agent is in disabled list, it's not launchable
    if (DISABLED_LAUNCH_AGENTS.includes(agent.id)) {
      return false;
    }

    // If agent requires external endpoint but doesn't have one, it's not launchable
    if (EXTERNAL_ENDPOINT_AGENTS.includes(agent.id) && !agent.endpoint) {
      return false;
    }

    return true;
  }, []);

  const handleVideoIconClick = (agent: Agents) => {
    setOpenVideoAgentId(agent.id);
  };

  const closeVideoPopover = () => {
    setOpenVideoAgentId(null);
  };

  // Get video URL for the currently open modal
  const openVideoUrl = useMemo(() => {
    if (openVideoAgentId === null) return null;
    const selectedAgent = displayedAgents.find(
      (a) => a.id === openVideoAgentId,
    );
    return selectedAgent ? getVideoUrl(selectedAgent) : null;
  }, [openVideoAgentId, displayedAgents, getVideoUrl]);

  const handleAgentInfoModal = (id: string | number) => {
    setOpenAgentModal(true);
    setAgentId(id);
  };
  const handleCloseAgentModal = () => setOpenAgentModal(false);

  return (
    <>
      <section className="featured">
        <div className="carousel-container">
          <div className="carousel-wrapper" ref={carouselWrapperRef}>
            <div
              className="featured-list"
              style={{
                ...(useExactSizing && { width: listWidthPx }),
                transform: useExactSizing
                  ? `translateX(-${translatePx}px)`
                  : `translateX(calc(-${currentSlide} * (100% / ${imagesPerView})))`,
              }}
            >
              {displayedAgents.map((a: Agents, index) => (
                <div
                  key={a.id}
                  className={`agent-card ${imageClasses[index % 5]}`}
                  style={{
                    background: agentCardBg[index % agentCardBg.length],
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    ...(useExactSizing
                      ? {
                          width: cardWidthPx,
                          minWidth: cardWidthPx,
                          flex: `0 0 ${cardWidthPx}px`,
                          height: "200px",
                        }
                      : {
                          width: `calc((100% - ${(imagesPerView - 1) * GAP_PX}px) / ${imagesPerView})`,
                          minWidth: `calc((100% - ${(imagesPerView - 1) * GAP_PX}px) / ${imagesPerView})`,
                          flex: `0 0 calc((100% - ${(imagesPerView - 1) * GAP_PX}px) / ${imagesPerView})`,
                        }),
                  }}
                >
                  {/* {getVideoUrl(a) && (
                  <>
                    <div className="agent-video-icon-wrapper">
                      <span className="agent-sneak-peek-text">
                        Watch it work
                      </span>
                      <button
                        className="agent-video-icon"
                        onMouseEnter={() => setPreviewVideoAgentId(a.id)}
                        onMouseLeave={() =>
                          setPreviewVideoAgentId((prev) =>
                            prev === a.id ? null : prev,
                          )
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVideoIconClick(a);
                        }}
                        aria-label="Preview agent video"
                      >
                        ▶
                      </button>
                    </div>

                    {previewVideoAgentId === a.id && (
                      <div className="agent-video-preview">
                        <video
                          src={getVideoUrl(a)!}
                          autoPlay
                          muted
                          loop
                          playsInline
                        />
                      </div>
                    )}
                  </>
                )} */}

                  {/* <div className="agent-image"></div> */}
                  <div className="agent-content-wrapper">
                    <div className="agent-action">
                      <img
                        src={agentIcons[index % agentIcons.length]}
                        alt="agent"
                        className="agentIcon"
                      />
                      {!COMING_SOON_AGENTS.includes(a.agent_name) && (
                        <button
                          className="launch"
                          onClick={() => handleSelectAgent(a)}
                          disabled={!isAgentLaunchable(a)}
                          style={{
                            opacity: !isAgentLaunchable(a) ? 0.5 : 1,
                            cursor: !isAgentLaunchable(a)
                              ? "not-allowed"
                              : "pointer",
                          }}
                        >
                          <img
                            src={launchArrow}
                            alt="Agent"
                            className="agentArrow"
                          />
                        </button>
                      )}
                    </div>
                    <div className="agent-content-right">
                      <div className="agent-title">
                        {a.agent_name}
                        {a.agent_name ===
                          "Contract Review and Finalization Agent" && (
                          <div className="agent-sovereign-tag">Pre Beta</div>
                        )}
                        {a.agent_name ===
                          "Contract Request (SOW Creator) Agent" && (
                          <div className="agent-sovereign-tag">Pre Beta</div>
                        )}
                        {a.agent_name === "Template Review Agent" && (
                          <div className="agent-sovereign-tag">Pre Beta</div>
                        )}
                        {a.id === 44 && (
                          <div className="agent-sovereign-tag">Pre Beta</div>
                        )}
                        {a.id === 47 && (
                          <div className="agent-sovereign-tag">Pre Beta</div>
                        )}
                        {a.id === 26 && (
                          <div className="agent-sovereign-tag">Pre Beta</div>
                        )}
                        {COMING_SOON_AGENTS.includes(a.agent_name) && (
                          <div className="agent-sovereign-tag">Coming Soon</div>
                        )}
                      </div>
                      <div className="agent-footer">
                        <div className="agent-category">{a.category}</div>
                        <InfoOutlined
                          className="info-icon"
                          onClick={() => handleAgentInfoModal(a.id)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {maxSlides > 1 && (
          <div className="carousel-controls">
            <button
              className="carousel-btn carousel-btn-prev"
              onClick={prevSlide}
              aria-label="Previous slide"
              disabled={currentSlide === 0}
            >
              <img src={leftArrow} alt="Previous" />
            </button>

            <div className="carousel-dots">
              {Array.from({ length: maxSlides }).map((_, index) => (
                <button
                  key={index}
                  className={`carousel-dot ${
                    index === currentSlide ? "active" : ""
                  }`}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>

            <button
              className="carousel-btn carousel-btn-next"
              onClick={nextSlide}
              aria-label="Next slide"
              disabled={currentSlide >= maxSlides - 1}
            >
              <img src={rightArrow} alt="Next" />
            </button>
          </div>
        )}

        {openVideoUrl && (
          <Modal
            open={true}
            onClose={closeVideoPopover}
            className="agent-video-modal"
            aria-labelledby="agent-video-modal-title"
            aria-describedby="agent-video-modal-description"
            slotProps={{
              backdrop: {
                sx: { backgroundColor: "rgba(0, 0, 0, 0.6)" },
              },
            }}
          >
            <div className="agent-video-modal-content">
              <button
                className="agent-video-modal-close"
                onClick={(e) => {
                  e.stopPropagation();
                  closeVideoPopover();
                }}
                aria-label="Close video modal"
              >
                ✕
              </button>
              <video
                src={openVideoUrl}
                controls
                autoPlay
                className="agent-video-modal-player"
              />
            </div>
          </Modal>
        )}
      </section>
      <AgentInfoModal
        open={openAgentModal}
        onClose={handleCloseAgentModal}
        agentId={agentId}
        onAgentClick={onAgentClick}
      />
    </>
  );
}
